import classNames from 'classnames';
import Button from 'react-bootstrap/Button';
import Image from 'components/Image';
import Link from 'components/Link';
import xss from 'utils/xss';
import styles from './Content2Up.module.scss';

export function Content2Up({
  anchorId,
  badge: { title: badgeTitle, url: badgeUrl } = {},
  backgroundColor,
  bodyText,
  children,
  ctaOnClick,
  ctaText,
  ctaUrl,
  customHeadingClassName,
  customPaddingClassName,
  heading,
  headingTag,
  hero: { title: heroTitle, url: heroUrl, link: heroLink } = {},
  isRowReverse = false,
  isSetDangerously = false,
  linkText,
  linkUrl,
  textColor = 'Navy',
  useSmallHeading = false,
}) {
  const bodyTextSanitized = isSetDangerously ? (
    // eslint-disable-next-line react/no-danger
    <div dangerouslySetInnerHTML={{ __html: xss(bodyText) }} />
  ) : (
    <p>{bodyText}</p>
  );
  const direction = isRowReverse
    ? 'flex-column flex-lg-row-reverse'
    : 'flex-column flex-lg-row';
  const heroClass = isRowReverse ? styles.heroReverse : styles.hero;

  const linkStyle = ctaUrl
    ? `${styles.link} ${styles.marginLeft}`
    : `${styles.link}`;

  const HeadingTag = headingTag || 'h2';

  const useDarkText = !textColor || textColor === 'Navy';

  const showCtaDiv = ctaUrl || linkUrl || ctaOnClick;

  return (
    <section
      id={anchorId}
      className={styles.section}
      style={{ background: backgroundColor || '#fff' }}
    >
      <div
        className={classNames(
          `d-flex ${direction} position-relative align-items-center justify-content-center text-primary`,
          {
            [styles.twoUp]: !customPaddingClassName,
            [customPaddingClassName]: Boolean(customPaddingClassName),
          }
        )}
      >
        {heroLink ? (
          <Link href={heroLink}>
            <Image
              objectFit="contain"
              height={427}
              width={350}
              alt={heroTitle}
              className={heroClass}
              src={heroUrl}
              srcSet={`${heroUrl}?width=350 350w, ${heroUrl}?width=450 450w, ${heroUrl}?width=592 592w`}
            />
          </Link>
        ) : (
          <Image
            objectFit="contain"
            height={427}
            width={350}
            alt={heroTitle}
            className={heroClass}
            src={heroUrl}
            srcSet={`${heroUrl}?width=350 350w, ${heroUrl}?width=450 450w, ${heroUrl}?width=592 592w`}
          />
        )}

        <div
          className={classNames(
            `d-flex flex-column justify-content-center`,
            styles.bodyText,
            { [styles.darkText]: useDarkText, [styles.lightText]: !useDarkText }
          )}
        >
          <div className="d-flex align-items-center mt-12 mt-lg-0">
            {badgeUrl && (
              <div className="ms-0 me-10">
                <Image
                  width={80}
                  height={80}
                  alt={badgeTitle}
                  className={styles.badge}
                  src={badgeUrl}
                />
              </div>
            )}
            <div
              className={classNames({
                [styles.small]: useSmallHeading && !customHeadingClassName,
                [styles.large]: !useSmallHeading && !customHeadingClassName,
              })}
            >
              <HeadingTag
                className={classNames('mb-0', {
                  'lh-1': !customHeadingClassName,
                  [`${customHeadingClassName}`]: Boolean(
                    customHeadingClassName
                  ),
                })}
              >
                {heading}
              </HeadingTag>
            </div>
          </div>
          <div className={classNames('mt-8', styles.bodyText)}>
            {bodyTextSanitized}
          </div>
          {showCtaDiv && (
            <div className="d-flex flex-row mt-8">
              {ctaOnClick && (
                <Button
                  className={styles.cta}
                  data-value="content-page-cta-element"
                  type="button"
                  variant="secondary"
                  onClick={ctaOnClick}
                >
                  {ctaText}
                </Button>
              )}
              {ctaUrl && (
                <Link
                  href={ctaUrl}
                  className={classNames(
                    `font-sofia fw-700 bg-secondary text-primary text-uppercase`,
                    styles.cta
                  )}
                  data-value="content-page-cta-element"
                >
                  {ctaText}
                </Link>
              )}
              {linkUrl && (
                <Link
                  href={linkUrl}
                  className={classNames(
                    `font-roboto fw-400 text-primary`,
                    linkStyle,
                    {
                      [styles.darkText]: useDarkText,
                      [styles.lightText]: !useDarkText,
                    }
                  )}
                  data-value="content-page-cta-element"
                >
                  {linkText}
                </Link>
              )}
            </div>
          )}
          <div>{children}</div>
        </div>
      </div>
    </section>
  );
  /* eslint-enable @next/next/no-img-element */
}

export default Content2Up;
